import { PlanSchema, type Proposal } from '@domain/plan.ts'
import type { FamilyState } from '@domain/state.ts'
import { childrenOf } from '@domain/state.ts'

/**
 * The one place this app talks to a machine outside itself.
 *
 * Everything else in Misión Hogar runs on the tablet with the aeroplane mode on.
 * This does not, and that is a real change in what the app is, so it is opt-in,
 * it lives behind the PIN, and the screen that uses it says out loud what
 * leaves the device.
 *
 * It talks to this app's own small backend, not to a model. That indirection is
 * not ceremony: the backend owns the prompt, holds the queue for a GPU that
 * generates one answer at a time, and is the only place that can ever know who
 * is entitled to ask. Swapping the machine behind it — this house's card today,
 * something else tomorrow — changes nothing here.
 *
 * It is served under `/api` on this same origin, which is why the page's
 * security policy is still `connect-src 'self'`: the tablet has no permission
 * to reach any other host on the internet, and does not need one.
 *
 * What leaves: the sentences the parent types, plus a summary of the current
 * configuration — the children's first names, the task names and their steps,
 * and the shop prices. No photos, no history of who did what, no coin balances.
 *
 * The reply is never trusted. It is parsed against `PlanSchema`, and whatever
 * survives is then run through the domain's own commands before a parent is
 * shown a single word of it.
 */

export interface AssistantConfig {
  readonly url: string
  readonly token: string | undefined
}

export type AssistantFailure =
  | 'no-configurado'
  | 'sin-conexion'
  | 'tardo-demasiado'
  | 'respuesta-ilegible'
  | 'rechazado'
  | 'sin-modelo'

export type AssistantReply =
  | { readonly ok: true; readonly proposals: readonly Proposal[] }
  | { readonly ok: false; readonly failure: AssistantFailure }

/**
 * Generous, but not because the model is slow. Measured on the machine this
 * runs against: ~70 tokens a second, and a real plan comes back in under two
 * seconds. The headroom is for the queue in front of it, which is what a second
 * family asking at the same moment looks like.
 */
const TIMEOUT_MS = 90_000

/** How the server answers a request it will not or cannot serve. */
const FAILURE_BY_STATUS: Readonly<Record<number, AssistantFailure>> = {
  400: 'respuesta-ilegible',
  401: 'rechazado',
  403: 'rechazado',
  503: 'sin-modelo',
  502: 'sin-modelo',
  504: 'tardo-demasiado',
}

/**
 * What the model is allowed to know. Deliberately small: names and shapes, never
 * how any particular day went. Built field by field rather than spread from
 * state, so that adding something to the domain never silently adds it here.
 */
export function snapshotOf(state: FamilyState): {
  children: { name: string; tasks: unknown[] }[]
  shop: unknown[]
} {
  const children = childrenOf(state).map((child) => {
    const tasks = [...state.tasks.values()]
      .filter((t) => t.memberId === child.id && !t.archived)
      .map((t) => ({
        name: t.name,
        kind: t.kind,
        ...(t.moment !== undefined ? { moment: t.moment } : {}),
        steps: t.steps.map((s) => s.text),
      }))
    return { name: child.name, tasks }
  })

  const shop = [...state.rewards.values()]
    .filter((r) => r.active)
    .map((r) => ({ name: r.name, price: r.price, kind: r.kind }))

  return { children, shop }
}

/**
 * Asks for a plan. Never throws: every failure is a value, because this runs on
 * a tablet that is usually offline on purpose.
 */
export async function askForPlan(
  config: AssistantConfig,
  state: FamilyState,
  ask: string,
  signal?: AbortSignal,
): Promise<AssistantReply> {
  if (config.url.trim().length === 0) return { ok: false, failure: 'no-configurado' }

  const timeout = new AbortController()
  const timer = setTimeout(() => {
    timeout.abort()
  }, TIMEOUT_MS)
  signal?.addEventListener('abort', () => {
    timeout.abort()
  })

  try {
    const response = await fetch(`${config.url.replace(/\/+$/, '')}/ia/plan`, {
      method: 'POST',
      signal: timeout.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(config.token !== undefined && config.token.length > 0
          ? { Authorization: `Bearer ${config.token}` }
          : {}),
      },
      body: JSON.stringify({ ask: ask.trim(), snapshot: snapshotOf(state) }),
    })

    if (!response.ok) {
      return { ok: false, failure: FAILURE_BY_STATUS[response.status] ?? 'rechazado' }
    }

    const parsed = PlanSchema.safeParse(await response.json())
    if (!parsed.success) return { ok: false, failure: 'respuesta-ilegible' }
    return { ok: true, proposals: parsed.data.changes }
  } catch (cause) {
    if (cause instanceof SyntaxError) return { ok: false, failure: 'respuesta-ilegible' }
    if (timeout.signal.aborted) return { ok: false, failure: 'tardo-demasiado' }
    return { ok: false, failure: 'sin-conexion' }
  } finally {
    clearTimeout(timer)
  }
}
